"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ShieldCheck, Thermometer, Wind, Droplets, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
export default function RemplacementUrethanPortePatioMontreal() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const benefits = [{
    icon: <Wind className="size-6 text-secondary" />,
    title: "Seamless Design",
    description: "Custom-fitted gutters that eliminate leaks and clogs permanently."
  }, {
    icon: <Thermometer className="size-6 text-secondary" />,
    title: "Foundation Protection",
    description: "Expertly diverted water flow to prevent costly structural damage."
  }, {
    icon: <ShieldCheck className="size-6 text-secondary" />,
    title: "Enhanced Curb Appeal",
    description: "Sleek, modern profiles that complement your home's architecture."
  }, {
    icon: <Droplets className="size-6 text-secondary" />,
    title: "Water Damage Prevention",
    description: "Protect your foundation. Prevent $5,000+ in water damage."
  }];
  const breadcrumbs = [{
    label: "Montgomery County",
    to: "/ServiceAreasPage"
  }, {
    label: "Howard County",
    to: "/ServiceAreasPage"
  }, {
    label: "Frederick County",
    to: "/ServiceAreasPage"
  }, {
    label: "Northern Virginia",
    to: "/ServiceAreasPage"
  }, {
    label: "View All Service Areas",
    to: "/ServiceAreasPage"
  }];
  return <section data-section-id="252456" id="gutter-installation-hero" className="relative bg-primary text-primary-foreground py-20 lg:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-lg bg-secondary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-lg bg-white/5 blur-[100px]" />
      </div>

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col gap-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-white/10 border border-white/20 text-sm font-medium text-primary-100">
                <span className="flex h-2 w-2 rounded-md bg-secondary"></span>
                Premium Exterior Solutions
              </div>
              <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white">
                Seamless Gutters That <span className="text-secondary">Actually Work</span> Across Maryland & Northern Virginia
              </h1>
              
              <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs md:text-sm font-medium text-primary-100/70">
                {breadcrumbs.map((crumb, i) => <React.Fragment key={i}>
                    <Link to={crumb.to} className="hover:text-secondary transition-colors">
                      {crumb.label}
                    </Link>
                    {i < breadcrumbs.length - 1 && <ChevronRight className="size-3 opacity-50" />}
                  </React.Fragment>)}
              </nav>

              <p className="font-default text-lg md:text-xl text-primary-100/90 max-w-xl leading-relaxed">
                Protect your home's foundation and boost curb appeal with our gutters custom-fit to your home in Montgomery County, Howard County, Frederick County, and Northern Virginia. Durable, leak-proof, and built to last.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
              {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex gap-4 items-start">
                  <div className="shrink-0 p-3 rounded-md bg-white/5 border border-white/10">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="font-default font-semibold text-white mb-1">{benefit.title}</h3>
                    <p className="font-default text-sm text-primary-100/80 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-6 flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-default text-base md:text-lg px-8 py-6 h-auto rounded-md shadow-lg transition-all hover:-translate-y-1">
                  <Link to="/ContactPage">Schedule Your Free Gutter Assessment</Link>
                </Button>
              </div>
              <p className="font-default text-sm text-primary-100/70 italic"><br /></p>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative rounded-md overflow-hidden border border-white/10 shadow-2xl shadow-black/50 group">
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent z-10" />
              <AspectRatio ratio={4 / 3}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/seamless_gutter_installation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/seamless_gutter_installation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/seamless_gutter_installation.png-wvc-sizes" alt="Professional seamless gutter installation on a modern home" className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </AspectRatio>
              
              {}
              <div className="absolute bottom-6 left-6 z-20 bg-white/10 backdrop-blur-md border border-white/20 rounded-md p-4 shadow-lg flex items-center gap-4">
                <div className="flex items-center justify-center size-12 rounded-md bg-secondary/20 text-secondary">
                  <ShieldCheck className="size-6" />
                </div>
                <div>
                  <p className="text-white font-bold font-default text-lg">Lifetime Workmanship Guarantee</p>
                  <p className="text-primary-100 text-sm">On Workmanship</p>
                </div>
              </div>
            </div>
            
            {}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-[radial-gradient(circle,rgba(255,255,255,0.1)_2px,transparent_2px)] bg-[size:8px_8px] -z-10" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[radial-gradient(circle,rgba(251,146,60,0.2)_2px,transparent_2px)] bg-[size:8px_8px] -z-10" />
          </div>

        </div>
      </div>
    </section>;
}